import React, { useState, useEffect } from 'react';
import '../styles/FormBuilder.css';

const FIELD_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone number' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' }
];

// Display label for any field type, including the special default date types.
const typeLabel = (t) => {
  if (t === 'checkin' || t === 'checkout') return 'Date (built-in)';
  return FIELD_TYPES.find(ft => ft.value === t)?.label || t;
};

export default function FormBuilder({ propertyId }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [adding, setAdding] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ fieldLabel: '', fieldType: 'text', isRequired: false, options: '' });

  const [draft, setDraft] = useState({ fieldLabel: '', fieldType: 'text', isRequired: false, options: '' });

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const loadFields = async () => {
    setError('');
    try {
      const response = await fetch(`/api/forms/${propertyId}/fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFields(data.fields || []);
      } else {
        setError('Failed to load fields');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 2500); };

  // ---- Reorder ----
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= fields.length) return;

    const next = [...fields];
    [next[index], next[target]] = [next[target], next[index]];
    setFields(next); // optimistic

    setSavingOrder(true);
    setError('');
    try {
      const response = await fetch(`/api/forms/${propertyId}/fields-order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ orderedIds: next.map(f => f.id) })
      });
      if (response.ok) {
        const data = await response.json();
        setFields(data.fields || next);
      } else {
        setError('Failed to save order');
        loadFields();
      }
    } catch (err) {
      setError('Error: ' + err.message);
      loadFields();
    } finally {
      setSavingOrder(false);
    }
  };

  // ---- Add ----
  const handleAddField = async (e) => {
    e.preventDefault();
    setError('');
    if (!draft.fieldLabel.trim()) { setError('Give the field a label'); return; }

    setAdding(true);
    try {
      const body = {
        fieldLabel: draft.fieldLabel.trim(),
        fieldType: draft.fieldType,
        isRequired: draft.isRequired,
        options: draft.fieldType === 'select'
          ? draft.options.split(',').map(o => o.trim()).filter(Boolean) : []
      };
      const response = await fetch(`/api/forms/${propertyId}/fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        flash(`Added "${body.fieldLabel}"`);
        setDraft({ fieldLabel: '', fieldType: 'text', isRequired: false, options: '' });
        loadFields();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to add field');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setAdding(false);
    }
  };

  // ---- Edit ----
  const startEdit = (f) => {
    setEditingId(f.id);
    setEditDraft({
      fieldLabel: f.fieldLabel,
      fieldType: f.fieldType,
      isRequired: f.isRequired,
      options: (f.options || []).join(', ')
    });
  };

  const cancelEdit = () => { setEditingId(null); };

  const saveEdit = async (f) => {
    setError('');
    if (!editDraft.fieldLabel.trim()) { setError('Label cannot be empty'); return; }

    const body = { fieldLabel: editDraft.fieldLabel.trim() };
    if (!f.isDefault) {
      body.fieldType = editDraft.fieldType;
      body.isRequired = editDraft.isRequired;
      body.options = editDraft.fieldType === 'select'
        ? editDraft.options.split(',').map(o => o.trim()).filter(Boolean) : [];
    } else if (f.fieldType === 'textarea') {
      body.isRequired = editDraft.isRequired; // Notes can toggle required
    }

    try {
      const response = await fetch(`/api/forms/${propertyId}/fields/${f.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        flash('Saved');
        setEditingId(null);
        loadFields();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to save');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  // ---- Delete ----
  const handleDelete = async (f) => {
    if (f.isDefault) return;
    if (!window.confirm(`Remove the "${f.fieldLabel}" field? Existing blocks keep their saved data.`)) return;
    setError('');
    try {
      const response = await fetch(`/api/forms/${propertyId}/fields/${f.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) { flash('Field removed'); loadFields(); }
      else { const err = await response.json(); setError(err.error || 'Failed to remove'); }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading…</div>;

  return (
    <div className="form-builder">
      <h2>Manual Block Form</h2>
      <p>These fields appear when adding a manual block, in this order. Use the arrows to reorder. Built-in fields (🔒) can be renamed and moved but not deleted.</p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="fb-section">
        <div className="fb-field-list">
          {fields.map((f, index) => (
            <div key={f.id} className={`fb-field-row ${f.isDefault ? 'fb-locked' : ''}`}>
              {editingId === f.id ? (
                <div className="fb-edit-area">
                  <div className="fb-add-row">
                    <input
                      type="text"
                      className="fb-input-label"
                      value={editDraft.fieldLabel}
                      onChange={(e) => setEditDraft({ ...editDraft, fieldLabel: e.target.value })}
                      autoFocus
                    />
                    {!f.isDefault && (
                      <select
                        className="fb-input-type"
                        value={editDraft.fieldType}
                        onChange={(e) => setEditDraft({ ...editDraft, fieldType: e.target.value })}
                      >
                        {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                      </select>
                    )}
                  </div>
                  {!f.isDefault && editDraft.fieldType === 'select' && (
                    <input
                      type="text"
                      className="fb-input-options"
                      placeholder="Choices, comma separated"
                      value={editDraft.options}
                      onChange={(e) => setEditDraft({ ...editDraft, options: e.target.value })}
                    />
                  )}
                  {(!f.isDefault || f.fieldType === 'textarea') && (
                    <label className="fb-check">
                      <input
                        type="checkbox"
                        checked={editDraft.isRequired}
                        onChange={(e) => setEditDraft({ ...editDraft, isRequired: e.target.checked })}
                      />
                      Required
                    </label>
                  )}
                  <div className="fb-edit-actions">
                    <button className="fb-add-btn" onClick={() => saveEdit(f)}>Save</button>
                    <button className="fb-cancel-btn" onClick={cancelEdit}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="fb-reorder">
                    <button disabled={index === 0 || savingOrder} onClick={() => move(index, -1)} title="Move up">▲</button>
                    <button disabled={index === fields.length - 1 || savingOrder} onClick={() => move(index, 1)} title="Move down">▼</button>
                  </div>
                  <div className="fb-field-main">
                    <span className="fb-field-label">{f.fieldLabel}</span>
                    <span className="fb-field-type">{typeLabel(f.fieldType)}</span>
                    {f.isRequired && <span className="fb-required">Required</span>}
                    {f.isDefault && <span className="fb-lock" title="Built-in field">🔒</span>}
                  </div>
                  <div className="fb-row-actions">
                    <button className="fb-edit" onClick={() => startEdit(f)}>Edit</button>
                    {!f.isDefault && (
                      <button className="fb-remove" onClick={() => handleDelete(f)}>Remove</button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="fb-section fb-add">
        <h3>Add a field</h3>
        <form onSubmit={handleAddField}>
          <div className="fb-add-row">
            <input
              type="text"
              className="fb-input-label"
              placeholder="Field label (e.g. Guest name, Phone)"
              value={draft.fieldLabel}
              onChange={(e) => setDraft({ ...draft, fieldLabel: e.target.value })}
            />
            <select
              className="fb-input-type"
              value={draft.fieldType}
              onChange={(e) => setDraft({ ...draft, fieldType: e.target.value })}
            >
              {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
            </select>
          </div>
          {draft.fieldType === 'select' && (
            <input
              type="text"
              className="fb-input-options"
              placeholder="Dropdown choices, comma separated (e.g. Yes, No, Maybe)"
              value={draft.options}
              onChange={(e) => setDraft({ ...draft, options: e.target.value })}
            />
          )}
          <label className="fb-check">
            <input
              type="checkbox"
              checked={draft.isRequired}
              onChange={(e) => setDraft({ ...draft, isRequired: e.target.checked })}
            />
            Make this field required
          </label>
          <button type="submit" className="fb-add-btn" disabled={adding}>
            {adding ? 'Adding…' : 'Add field'}
          </button>
        </form>
      </div>
    </div>
  );
}
